import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { IParams } from '../model/http';

export const initListsParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

export const initItemsInvoiceParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

/** Only what is bought — the backend calculates the amounts and signs the LiqPay form */
export interface CreatePaymentV2Request {
  /** LiqPay method, or 'free' for a change that costs nothing */
  method: string;
  tariff?: string;
  /** Period of the tariff tab: the tariff and the company channels are paid for it */
  months?: number;
  /** Channels added to the current period; with `months` only on trial/demo/free, where the count is chosen with the tariff */
  channels?: { channel_type: string; count: number }[];
  tokens?: number;
  /** Total the user saw; the backend charges its own price and only compares it with this one */
  expected_amount: number;
}

export interface CreatePaymentV2Response {
  id: string;
  amount: number;
  /** Form fields to POST to `action_url`; absent for a 'free' change */
  liqpay?: { action_url: string; data: string; signature: string };
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private readonly destroy$ = new Subject<null>();

  constructor(
    private readonly http: HttpClient,
  ) {
  }

  private readonly historySubject$ = new BehaviorSubject<any>({});
  public readonly history$ = this.historySubject$.asObservable();

  public readonly isLoader$ = new BehaviorSubject<boolean>(false);

  public queryListsParams = { ...initListsParams };
  public queryListInvoicesParams = { ...initItemsInvoiceParams };

  create(payload: any): Observable<any> {
    return this.http.post<any>(`${environment.baseURL}/payment/create`, payload)
      .pipe(map(res => res.data));
  }

  createV2(payload: CreatePaymentV2Request): Observable<CreatePaymentV2Response> {
    return this.http.post<any>(`${environment.baseURL}/payment`, payload)
      .pipe(map(res => res.data));
  }

  activateChannel(paymentId: string): Observable<any> {
    return this.http.post<any>(`${environment.baseURL}/payment/activate-v2`, { payment_id: paymentId })
      .pipe(map(res => res.data));
  }

  liqpay(url: string, data: any) {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    return this.http.post<any>(url, data, { headers })
      .pipe(map(res => res.data));
  }

  activateTariff(payload: { tariff: string, promo_code: string }) {
    return this.http.post<any>(`${environment.baseURL}/payment/activate`, payload)
      .pipe(map(res => res.data));
  }

  getPaymentHistory(params: any) {
    return this.http.get<any>(`${environment.baseURL}/payment/history`, { params })
      .pipe(map(res => res.data));
  }

  loadHistory() {
    this.isLoader$.next(true);
    return this.getPaymentHistory(this.queryListsParams)
      .pipe(tap((data) => {
        this.historySubject$.next(data);
        this.isLoader$.next(false);
      }));
  }

  setQueryListsParams(params: IParams) {
    const { sort, order, search, take, page } = params;
    this.queryListsParams = {
      sort: sort || typeof (sort) === 'string' ? sort : this.queryListsParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryListsParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryListsParams.search,
      take: take || this.queryListsParams.take,
      page: page || this.queryListsParams.page,
    };
    return this.loadHistory();
  }
}
